var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { getTextContent } from '@simpledotink/notion-utils';
import { useNotionContext } from '../context';
import { LazyImage } from './lazy-image';
import { parseImage } from '../libs/embed';
var isServer = typeof window === 'undefined';
var types = [
    'video',
    'image',
    'embed',
    'figma',
    'typeform',
    'excalidraw',
    'maps',
    'tweet',
    'pdf',
    'gist',
    'codepen',
    'drive'
];
export var Asset = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
    var block = _a.block, children = _a.children;
    var _t = useNotionContext(), recordMap = _t.recordMap, mapImageUrl = _t.mapImageUrl, components = _t.components;
    if (!block || !types.includes(block.type)) {
        return null;
    }
    var style = {
        position: 'relative',
        display: 'flex',
        justifyContent: ((_b = block === null || block === void 0 ? void 0 : block.format) === null || _b === void 0 ? void 0 : _b.block_alignment) || 'center',
        alignSelf: 'center',
        width: '100%',
        maxWidth: '100vw',
        flexDirection: 'column'
    };
    var assetStyle = {};
    if (block.format) {
        var _u = block.format, block_aspect_ratio = _u.block_aspect_ratio, block_height = _u.block_height, block_width = _u.block_width, block_full_width = _u.block_full_width, block_page_width = _u.block_page_width, block_preserve_scale = _u.block_preserve_scale;
        if (block_full_width || block_page_width) {
            if (block_full_width) {
                style.width = '100vw';
                style.position = 'relative';
                style.left = 'calc(0px - var(--body-padding))';
            }
            else {
                style.width = '100%';
            }
            if (block_aspect_ratio && block.type !== 'image') {
                style.paddingBottom = "".concat(block_aspect_ratio * 100, "%");
            }
            else if (block_height) {
                style.height = block_height;
            }
            else if (block_preserve_scale) {
                if (block.type === 'image') {
                    style.height = '100%';
                }
                else {
                    // TODO: this is just a guess
                    style.paddingBottom = '75%';
                    style.minHeight = 100;
                }
            }
        }
        else {
            if (block_width) {
                style.width = block_width;
                var pageId = Object.keys(recordMap.block)[0];
                if (pageId == block.parent_id) {
                    style.minWidth = block_width;
                    style.position = 'relative';
                    style.left = 'calc(0px - var(--body-padding))';
                }
            }
            if (block_preserve_scale && block.type !== 'image') {
                style.paddingBottom = '50%';
                style.minHeight = 100;
            }
            else {
                if (block_height && block.type !== 'image') {
                    style.height = block_height;
                }
            }
        }
        if (block.type === 'image') {
            assetStyle.objectFit = 'cover';
            style.alignSelf = 'flex-start';
        }
        else if (block_preserve_scale) {
            assetStyle.objectFit = 'contain';
        }
    }
    var source = (_e = (_d = (_c = block.properties) === null || _c === void 0 ? void 0 : _c.source) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e[0];
    var content = null;
    var imageLinkData = null;
    if (block.type === 'tweet') {
        var src = source;
        if (!src)
            return null;
        var id = src.split('?')[0].split('/').pop();
        if (!id)
            return null;
        content = (React.createElement("div", { style: __assign(__assign({}, assetStyle), { maxWidth: 420, width: '100%', marginLeft: 'auto', marginRight: 'auto' }) },
            React.createElement(components.tweet, { id: id })));
    }
    else if (block.type === 'pdf') {
        style.overflow = 'auto';
        style.padding = '8px 16px';
        style.background = 'rgb(226, 226, 226)';
        if (!isServer) {
            var signedUrl = (_f = recordMap.signed_urls) === null || _f === void 0 ? void 0 : _f[block.id];
            if (!signedUrl) {
                var attachment = (_j = (_h = (_g = block.properties) === null || _g === void 0 ? void 0 : _g.source) === null || _h === void 0 ? void 0 : _h[0]) === null || _j === void 0 ? void 0 : _j[0];
                if (!attachment) {
                    return null;
                }
                signedUrl = "https://www.notion.so/signed/".concat(attachment, "?table=block&id=").concat(block.id, "&cache=v2&spaceId=").concat(block.space_id, "#toolbar=0&navpanes=0");
                content = (React.createElement("iframe", { src: signedUrl, width: '100%', height: '100%', frameBorder: '0', title: 'PDF Embed' }));
            }
            else {
                if (signedUrl.indexOf('spaceId') == -1)
                    signedUrl += "&spaceId=".concat(block.space_id);
                content = React.createElement(components.pdf, { file: signedUrl });
            }
        }
    }
    else if (block.type === 'embed' ||
        block.type === 'video' ||
        block.type === 'figma' ||
        block.type === 'typeform' ||
        block.type === 'gist' ||
        block.type === 'maps' ||
        block.type === 'excalidraw' ||
        block.type === 'codepen' ||
        block.type === 'drive') {
        var signedUrl = recordMap.signed_urls[block.id];
        var extensions = [
            'MPG',
            'MP2',
            'MPEG',
            'MPE',
            'MPV',
            'OGG',
            'MP4',
            'M4P',
            'M4V',
            'M4A',
            'F4V',
            'F4A',
            'AVI',
            'WMV',
            'MOV',
            'QT',
            'FLV',
            'SWF',
            'AVCHD',
            'MKV',
            'WEBM'
        ].map(function (m) { return m.toLowerCase(); });
        // get the video extension from the url
        var extension = (_l = (_k = signedUrl === null || signedUrl === void 0 ? void 0 : signedUrl.split('?')[0]) === null || _k === void 0 ? void 0 : _k.split('.')) === null || _l === void 0 ? void 0 : _l.pop();
        if (block.type === 'video' &&
            signedUrl &&
            signedUrl.indexOf('youtube') < 0 &&
            signedUrl.indexOf('youtu.be') < 0 &&
            signedUrl.indexOf('vimeo') < 0 &&
            signedUrl.indexOf('wistia') < 0 &&
            signedUrl.indexOf('loom') < 0 &&
            signedUrl.indexOf('videoask') < 0 &&
            signedUrl.indexOf('getcloudapp') < 0 &&
            extension &&
            extensions.includes(extension)) {
            if (signedUrl.indexOf('spaceId') == -1)
                signedUrl += "&spaceId=".concat(block.space_id);
            content = (React.createElement("video", { playsInline: true, controls: true, preload: 'metadata', style: __assign(__assign({}, assetStyle), { height: 'auto' }), src: signedUrl + '#t=0.01', title: block.type }));
            style.height = 'auto';
        }
        else {
            var src = (_o = (_m = block.format) === null || _m === void 0 ? void 0 : _m.display_source) !== null && _o !== void 0 ? _o : source;
            if (src) {
                if (block.type === 'gist' && !src.endsWith('.pibb')) {
                    src = "".concat(src, ".pibb");
                }
                if (block.type === 'gist') {
                    assetStyle.width = '100%';
                    style.paddingBottom = '50%';
                    // TODO: GitHub gists do not resize their height properly
                    content = (React.createElement("iframe", { style: assetStyle, className: 'notion-asset-object-fit', src: src, title: 'GitHub Gist', frameBorder: '0', 
                        // TODO: is this sandbox necessary?
                        // sandbox='allow-scripts allow-popups allow-top-navigation-by-user-activation allow-forms allow-same-origin'
                        // this is important for perf but react's TS definitions don't seem to like it
                        loading: 'lazy', scrolling: 'auto' }));
                }
                else {
                    /*
                    if this a youtube video that contains the /watch route instead of /embed. Replace the /watch route with the /embed route if not, youtube will complete of sameorigin error. See more here  https://stackoverflow.com/questions/25661182/embed-youtube-video-refused-to-display-in-a-frame-because-it-set-x-frame-opti
                    */
                    if (/(youtube.com|youtu.be)\/watch/.test(src)) {
                        src = src.replace(/(youtube.com|youtu.be)\/watch\?v=/, 'youtube.com/embed/');
                    }
                    content = (React.createElement("iframe", { className: 'notion-asset-object-fit', style: assetStyle, src: src, title: "iframe ".concat(block.type), frameBorder: '0', 
                        // TODO: is this sandbox necessary?
                        // sandbox='allow-scripts allow-popups allow-top-navigation-by-user-activation allow-forms allow-same-origin'
                        allowFullScreen: true, 
                        // this is important for perf but react's TS definitions don't seem to like it
                        loading: 'lazy' }));
                }
            }
        }
    }
    else if (block.type === 'image') {
        var imageUrl = (_p = block === null || block === void 0 ? void 0 : block.format) === null || _p === void 0 ? void 0 : _p.display_source;
        if (!imageUrl && !source)
            return null;
        var src = mapImageUrl(imageUrl || source, block);
        var caption = getTextContent((_q = block.properties) === null || _q === void 0 ? void 0 : _q.caption);
        var alt = caption || 'notion image';
        style.height = 'auto';
        if ((_r = block.format) === null || _r === void 0 ? void 0 : _r.block_alignment) {
            switch ((_s = block.format) === null || _s === void 0 ? void 0 : _s.block_alignment) {
                case 'left':
                    style.alignSelf = 'flex-start';
                    break;
                case 'center':
                    style.alignSelf = 'center';
                    break;
                case 'right':
                    style.alignSelf = 'flex-end';
            }
        }
        imageLinkData = parseImage(caption);
        var image = (React.createElement(LazyImage, { src: src, alt: alt, zoomable: !imageLinkData.isLink, height: 0, style: assetStyle }));
        content = imageLinkData.isLink ? (React.createElement("a", __assign({}, imageLinkData.data), image)) : (image);
    }
    return (React.createElement("div", { style: {
            display: 'flex',
            width: '100%',
            flexWrap: 'wrap',
            justifyContent: style.justifyContent
        } },
        React.createElement("div", { style: style, className: 'remove-minwidth' },
            content,
            block.type === 'image' && !(imageLinkData === null || imageLinkData === void 0 ? void 0 : imageLinkData.isLink) && children),
        block.type !== 'image' && children));
};
//# sourceMappingURL=asset.js.map